import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useParams, useHistory, useLocation, Redirect } from "react-router"
import classNames from "classnames"
import Box from "@material-ui/core/Box"

import styles from "./SelectedFreelancerContainer.module.scss"
import FreelancerProfileSidebar from "../../components/FreelancerProfileSidebar/FreelancerProfileSidebar"
import FreelancerProfileSkeleton from "../../components/FreelancerProfileSkeleton/FreelancerProfileSkeleton"
import freelancerServices from "../../services/freelancerServices"
import { paths } from "../../routes/paths"
import services from "../../services"
import Breadcrumbs from "../../components/elements/Breadcrumbs"
import useBreadcrumbs from "./useBreadcrumbs"
import Alert from "@material-ui/lab/Alert"
import IconButton from "@material-ui/core/IconButton"
import CloseIcon from "@material-ui/icons/Close"
import { ADMIN } from "../../helpers/userRoles"
import FreelancerProfileTable from "../../components/ProfileTable/FreelancerProfileTable"
import Button from "../../components/Button/Button"
import { ReactComponent as HeartIcon } from "../../images/icons/heartIcon.svg"
import { ReactComponent as HeartFilledIcon } from "../../images/icons/heart-filled.svg"

const SelectedUserProfileContainer = () => {
  const location = useLocation()
  const listProfile = location.state?.profile
  const [data, setData] = useState(() =>
    listProfile ? { ...listProfile } : {},
  )
  const [isLoading, setIsLoading] = useState(true)
  const userInfo = useSelector((state) => state.user)
  const breadcurmbsPaths = useBreadcrumbs(data)
  const [reportAlertVisible, setReportAlertVisible] = useState(false)
  const [isFavorite, setIsFavorite] = useState(!!listProfile?.favorite)
  const { id } = useParams()
  const history = useHistory()

  const onBlock = (status) => {
    services.reportServices
      .blockUser({ user_uuid: data.uuid, block: status })
      .then(() => {
        setData({ ...data, blocked: status })
      })
      .catch(console.log)
  }

  const onReport = ({ title, description }) => {
    return services.reportServices
      .reportUser({
        title,
        description,
        reported_id: data.uuid,
        reported_type: "user",
      })
      .then((result) => {
        setReportAlertVisible(true)
        setData({ ...data, reported: true })
        setTimeout(() => setReportAlertVisible(false), 5000)
        return result
      })
  }

  useEffect(() => {
    setIsLoading(true)
    freelancerServices
      .getFreelancerProfile(id)
      .then((result) => {
        const api = result.data || {}
        setData({
          ...api,
          total_jobs: api.total_jobs ?? listProfile?.total_jobs,
          total_hours: api.total_hours ?? listProfile?.total_hours,
        })
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
        history.replace({ pathname: "/not-found", isActive: true })
      })
  }, [history, id, listProfile])

  useEffect(() => {
    if (typeof data?.favorite === "boolean") {
      setIsFavorite(data.favorite)
    }
  }, [data?.favorite])

  const onToggleFavorite = async () => {
    const identifier = data?.profile_key || data?.external_id || id
    if (!identifier) return

    const shouldFavorite = !isFavorite
    try {
      await services.favoriteServices.addRemoveToList({
        type: "freelancer",
        uuid: identifier,
        favorite: shouldFavorite,
        freelancerData: shouldFavorite
          ? {
              name: data?.name,
              title: data?.title,
              photo: data?.photo,
              description: data?.description,
            }
          : undefined,
      })
      setIsFavorite(shouldFavorite)
    } catch (e) {
      // keep UI unchanged on error
      console.log(e)
    }
  }

  const headerActions = (
    <div className={styles.freelancerHeaderActions}>
      <Button
        width="auto"
        height="40px"
        onClick={onToggleFavorite}
        buttonStyle="contained"
        className={styles.freelancerActionBtn}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {isFavorite ? <HeartFilledIcon /> : <HeartIcon />}
          <span>Favorite</span>
        </span>
      </Button>
      <Button
        width="auto"
        height="40px"
        buttonStyle="contained"
        component="a"
        href="mailto:ambassador@theb2t.com"
        className={styles.freelancerActionBtn}
      >
        Contact Account Executive
      </Button>
    </div>
  )

  const onOpenChat = async () => {
    const uuid = data.uuid

    if (data.chat) {
      history.push(paths.chats(data.chat))
    } else {
      services.chatServices
        .createChatRoom({ type: "freelancer", uuid: uuid })
        .then(({ data }) => {
          history.push(paths.chats(data.uuid))
        })
    }

    // if (!data.chat) {
    //     services.chatServices
    //         .createChatRoom({ type: "user", uuid: id })
    //         .then((result) => history.push(paths.chats(result.data.uuid)))
    //         .catch((error) => console.log(error))
    // } else {
    //     history.push(paths.chats(data.chat))
    // }
  }

  if (userInfo.uuid === id) {
    return <Redirect to={paths.individualProfile} />
  }

  if (data?.blocked && userInfo.role !== ADMIN) {
    return <Redirect to={paths.notFound} />
  }
  return (
    <div className={classNames("container", "fullWidth", styles.profileWrap)}>
      {reportAlertVisible && (
        <Alert
          variant="filled"
          severity="info"
          className={styles.alert}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setReportAlertVisible(false)
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          Your report was successfully sent.
        </Alert>
      )}

      {userInfo.role !== ADMIN && (
        <Breadcrumbs classes={styles.breadcrumbs} chain={breadcurmbsPaths} />
      )}

      {isLoading ? (
        <FreelancerProfileSkeleton />
      ) : (
        <Box
          display="flex"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ width: "100%" }}
        >
          <div className={styles.profileLeftColumn}>
            <div className={styles.profileInfo}>
              <FreelancerProfileSidebar
                data={data}
                onReport={onReport}
                reported={data.reported}
                userRole={userInfo.role}
                onBlock={onBlock}
                isBlocked={data.blocked}
              />
            </div>
          </div>
          <div className={styles.profileRightColumn}>
            <FreelancerProfileTable anotherUser data={data} shareButton={headerActions} />
          </div>
        </Box>
      )}
    </div>
  )
}

export default SelectedUserProfileContainer
